import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Sparkles, Loader2, Coins, ChevronRight, Wine, Compass, Heart, Lightbulb, NotebookPen, Brain } from "lucide-react";
import GuestPreview from "@/components/GuestPreview";

const PREVIEW_WINES = {
  similar: [
    { name: "Chablis 1er Cru Vaillons", producer: "Domaine Louis Michel", appellation: "Chablis 1er Cru", price_range_eur: "28–35 €", cepages: ["Chardonnay"], reason: "Même précision saline que vos Chablis village notés 16/20, avec plus de profondeur minérale grâce à la parcelle Vaillons.", pairing_idea: "Huîtres Gillardeau n°2, sole meunière, fromage de chèvre frais." },
    { name: "Sancerre Les Monts Damnés", producer: "Henri Bourgeois", appellation: "Sancerre", price_range_eur: "32 €", cepages: ["Sauvignon Blanc"], reason: "Vous aimez les blancs vifs et minéraux — ce Sancerre de coteau crayeux est dans la même lignée tendue.", pairing_idea: "Crottin de Chavignol, asperges blanches." },
    { name: "Champagne Blanc de Blancs Grand Cru", producer: "Pierre Péters", appellation: "Champagne", price_range_eur: "55 €", cepages: ["Chardonnay"], reason: "Pour prolonger votre goût des Chardonnay crayeux mais avec la bulle fine d'un Grand Cru du Mesnil.", pairing_idea: "Apéritif, saumon fumé, gambas grillées." },
  ],
  horizon: [
    { name: "Trousseau Plein Sud", producer: "Domaine de la Tournelle", appellation: "Arbois", price_range_eur: "26 €", cepages: ["Trousseau"], reason: "Vous n'avez encore noté aucun vin du Jura — ce Trousseau délicat et épicé est une porte d'entrée idéale.", pairing_idea: "Volaille au vin jaune, comté 24 mois." },
    { name: "Mondeuse Vieilles Vignes", producer: "Domaine Belluard", appellation: "Vin de Savoie", price_range_eur: "22 €", cepages: ["Mondeuse"], reason: "Cépage savoyard rare, sur le fruit et le poivre — pour élargir votre palette des rouges alpins.", pairing_idea: "Diots, gratin de crozets." },
    { name: "Savagnin Ouillé", producer: "Stéphane Tissot", appellation: "Côtes du Jura", price_range_eur: "30 €", cepages: ["Savagnin"], reason: "Sans le voile oxydatif : un blanc tendu, salin, qui va vous plaire si vous aimez les Chablis." },
  ],
  audrey_pick: { name: "Bourgogne Aligoté Cuvée Plantation", producer: "Domaine Sylvain Pataille", appellation: "Bourgogne Aligoté", price_range_eur: "24 €", cepages: ["Aligoté"], reason: "Mon coup de cœur : un Aligoté qui se prend pour un grand Chardonnay. À 24 €, c'est l'une des plus belles surprises de Marsannay.", pairing_idea: "Apéritif, escargots de Bourgogne, tartare de poisson." },
  profile_summary: "Vous êtes attiré·e par les blancs minéraux tendus de Bourgogne et de Loire, avec une préférence pour les Premier Cru bien situés. geoVINUM AI vous emmène explorer le Jura et la Savoie pour élargir votre palette.",
};

const DEMO_STEPS = [
  { title: "1. Précisez votre envie", body: "« Un blanc minéral pour ce soir » + budget 25€", icon: NotebookPen, color: "text-[#1F3A5F]" },
  { title: "2. geoVINUM AI croise votre carnet", body: "Profil gustatif + envie + budget → recommandations", icon: Brain, color: "text-[#E5276A]" },
  { title: "3. 7 cuvées sur-mesure", body: "3 dans vos goûts · 3 pour explorer · 1 coup de cœur", icon: Sparkles, color: "text-[#E5276A]" },
  { title: "4. Accords mets-vins inclus", body: "Chaque vin avec un accord parfait, pour ce soir", icon: Wine, color: "text-[#1F3A5F]" },
];

function WineCard({ w, tone = "pink", testId }) {
  const border = tone === "navy" ? "border-[#1F3A5F]" : "border-[#E5276A]";
  const bg = tone === "navy" ? "bg-white" : "bg-white";
  return (
    <div data-testid={testId} className={`rounded-2xl border-l-4 ${border} ${bg} border-y border-r border-[#1F3A5F]/10 p-5`}>
      <div className="flex items-start gap-2">
        <Wine className={`w-5 h-5 ${tone === "navy" ? "text-[#1F3A5F]" : "text-[#E5276A]"} shrink-0 mt-0.5`} />
        <div className="min-w-0">
          <h3 className="font-bold text-[#1F3A5F] text-base leading-tight">{w.name}</h3>
          <p className="text-xs text-[#8B95A1] mt-0.5">{w.producer} · {w.appellation}</p>
        </div>
        {w.price_range_eur && (
          <span className="ml-auto text-[10px] font-bold text-[#1F3A5F] bg-[#FBF7F3] px-2 py-0.5 rounded-full shrink-0">{w.price_range_eur}</span>
        )}
      </div>
      {w.cepages?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {w.cepages.slice(0, 3).map((c, i) => (
            <span key={i} className="text-[10px] font-bold text-[#E5276A] bg-[#FCE4ED] px-1.5 py-0.5 rounded-full">{c}</span>
          ))}
        </div>
      )}
      <p className="text-sm text-[#4A5568] mt-3 leading-relaxed italic">« {w.reason} »</p>
      {w.pairing_idea && <p className="text-xs text-[#1F3A5F] mt-2"><b>À table :</b> {w.pairing_idea}</p>}
    </div>
  );
}

export default function TasteSuggestions() {
  const { user } = useAuth();
  const { lang } = useT();
  const [mood, setMood] = useState("");
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tlp/taste/suggestions", {
        mood: mood || undefined,
        budget_eur: budget ? parseInt(budget) : undefined,
        lang,
      });
      setOut(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error === "insufficient_credits"
        ? "Crédits insuffisants — passez sur Amateur ou achetez un pack."
        : e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <GuestPreview
        kicker="Taste · Suggestions"
        kickerIcon={Sparkles}
        title="geoVINUM AI vous recommande"
        description="À partir de votre profil et de votre carnet, geoVINUM AI propose 3 cuvées dans vos goûts, 3 pour élargir l'horizon, et 1 coup de cœur osé — avec accords mets-vins."
        demoSteps={DEMO_STEPS}
        nextPath="/taste/suggestions"
        ctaTitle={<>Des suggestions <span className="text-[#E5276A]">faites pour vous</span></>}
        ctaSubtitle="Inscrivez-vous pour que geoVINUM AI croise votre profil, votre budget et votre envie du moment afin de vous proposer 7 cuvées sur-mesure."
        testId="taste-suggestions-preview"
      >
        <div className="rounded-2xl bg-[#FBF7F3] border-l-4 border-[#E5276A] p-4 mb-6">
          <p className="text-[#1F3A5F] italic text-sm">{PREVIEW_WINES.profile_summary}</p>
        </div>
        <h3 className="font-display text-lg text-[#1F3A5F] mb-3 inline-flex items-center gap-2"><Heart className="w-4 h-4 text-[#E5276A]" /> Dans vos goûts</h3>
        <div className="grid md:grid-cols-3 gap-3 mb-6">
          {PREVIEW_WINES.similar.map((w, i) => <WineCard key={i} w={w} tone="pink" testId={`preview-similar-${i}`} />)}
        </div>
        <h3 className="font-display text-lg text-[#1F3A5F] mb-3 inline-flex items-center gap-2"><Compass className="w-4 h-4 text-[#1F3A5F]" /> Élargir l'horizon</h3>
        <div className="grid md:grid-cols-3 gap-3 mb-6">
          {PREVIEW_WINES.horizon.map((w, i) => <WineCard key={i} w={w} tone="navy" testId={`preview-horizon-${i}`} />)}
        </div>
        <h3 className="font-display text-lg text-[#1F3A5F] mb-3 inline-flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> Coup de cœur de geoVINUM AI</h3>
        <WineCard w={PREVIEW_WINES.audrey_pick} tone="pink" testId="preview-audrey-pick" />
      </GuestPreview>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="taste-suggestions">
      <span className="chip-pink inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Taste · Suggestions</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">geoVINUM AI vous recommande</h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">À partir de votre carnet, geoVINUM AI propose 3 cuvées similaires, 3 pour élargir l'horizon, et 1 coup de cœur osé.</p>

      <div className="mt-8 app-card p-6 grid sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-2">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">Envie du moment (optionnel)</span>
          <input
            data-testid="suggestions-mood"
            value={mood} onChange={(e) => setMood(e.target.value)}
            placeholder="Ex : un blanc minéral pour ce soir, un rouge gourmand pour un grillade…"
            className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-2.5 text-sm text-[#1F3A5F]"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">Budget approx. (€, optionnel)</span>
          <input data-testid="suggestions-budget" type="number" min="5" max="500" value={budget} onChange={(e) => setBudget(e.target.value)}
            className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-2.5 text-sm text-[#1F3A5F]" />
        </label>
        <button onClick={run} disabled={busy} data-testid="suggestions-run"
          className="self-end inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Lancer (3 crédits)
        </button>
      </div>

      {out?.empty && (
        <div className="mt-8 app-card p-6 text-center" data-testid="suggestions-empty">
          <p className="text-sm text-[#4A5568]">{out.message}</p>
          <Link to="/carnet" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold">Enrichir mon carnet <ChevronRight className="w-4 h-4" /></Link>
        </div>
      )}

      {out && !out.empty && (
        <div className="mt-10 space-y-10" data-testid="suggestions-results">
          {out.profile_summary && (
            <div className="rounded-2xl bg-[#FBF7F3] border-l-4 border-[#E5276A] p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-1">Votre profil</div>
              <p className="text-[#1F3A5F] italic">{out.profile_summary}</p>
            </div>
          )}
          <section>
            <h2 className="font-display text-2xl text-[#1F3A5F] mb-4 inline-flex items-center gap-2"><Heart className="w-5 h-5 text-[#E5276A]" /> Dans vos goûts</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {(out.similar || []).map((w, i) => <WineCard key={i} w={w} tone="pink" testId={`suggestion-similar-${i}`} />)}
            </div>
          </section>
          <section>
            <h2 className="font-display text-2xl text-[#1F3A5F] mb-4 inline-flex items-center gap-2"><Compass className="w-5 h-5 text-[#1F3A5F]" /> Élargir l'horizon</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {(out.horizon || []).map((w, i) => <WineCard key={i} w={w} tone="navy" testId={`suggestion-horizon-${i}`} />)}
            </div>
          </section>
          {out.audrey_pick && (
            <section>
              <h2 className="font-display text-2xl text-[#1F3A5F] mb-4 inline-flex items-center gap-2"><Lightbulb className="w-5 h-5 text-amber-500" /> Coup de cœur de geoVINUM AI</h2>
              <WineCard w={out.audrey_pick} tone="pink" testId="suggestion-audrey-pick" />
            </section>
          )}
          {out.billing?.credits_remaining != null && (
            <p className="text-xs text-[#8B95A1] text-center"><Coins className="w-3 h-3 inline" /> {out.billing.credits_remaining} crédits restants</p>
          )}
        </div>
      )}
    </div>
  );
}
